import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import type { Package, SubscriptionView } from "@vereinfach/types";
import { api, mutate, errorMessage } from "../lib/api";
import { coverageLabelText, formatCHF, formatPeriodRange, monthsLabel } from "../lib/format";
import { Modal } from "../components/Modal";

const MONTH_NAMES = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

const DURATIONS = [1, 2, 3, 4, 6, 12, 24];
// Year-aligned packages must divide a year cleanly.
const YEAR_ALIGNED_DURATIONS = [1, 2, 3, 4, 6, 12];

type RefreshOffer = { packageId: string; ids: string[] };

export function PackagesPage({ tenantId }: { tenantId: string }) {
  const [packages, setPackages] = useState<Package[] | null>(null);
  const [subs, setSubs] = useState<SubscriptionView[] | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Package | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [refreshOffer, setRefreshOffer] = useState<RefreshOffer | null>(null);
  const [search, setSearch] = useState("");
  const [packageFilter, setPackageFilter] = useState<string>("all");
  const navigate = useNavigate();

  async function refresh() {
    const [p, s] = await Promise.all([
      api<{ packages: Package[] }>(`/tenants/${tenantId}/packages`),
      api<{ subscriptions: SubscriptionView[] }>(`/tenants/${tenantId}/subscriptions`),
    ]);
    setPackages(p.packages);
    setSubs(s.subscriptions);
  }

  useEffect(() => {
    setPackages(null);
    setSubs(null);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const running = useMemo(
    () => (subs ?? []).filter((s) => s.status === "active" || s.status === "paused"),
    [subs],
  );

  const runningByPackage = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of running) counts.set(s.package_id, (counts.get(s.package_id) ?? 0) + 1);
    return counts;
  }, [running]);

  async function archive(id: string) {
    if (!confirm("Beitrag archivieren? Laufende Beiträge der Mitglieder laufen weiter.")) return;
    try {
      await mutate(`/tenants/${tenantId}/packages/${id}`, {}, "DELETE");
      refresh();
    } catch (e) {
      alert(errorMessage(e));
    }
  }

  async function unarchive(id: string) {
    try {
      await mutate(`/tenants/${tenantId}/packages/${id}/unarchive`);
      refresh();
    } catch (e) {
      alert(errorMessage(e));
    }
  }

  async function refreshDrafts(offer: RefreshOffer) {
    try {
      await mutate(`/tenants/${tenantId}/packages/${offer.packageId}/refresh-drafts`, {
        invoice_ids: offer.ids,
      });
      setRefreshOffer(null);
      refresh();
    } catch (e) {
      alert(errorMessage(e));
    }
  }

  const active = (packages ?? []).filter((p) => p.is_archived === 0);
  const archived = (packages ?? []).filter((p) => p.is_archived === 1);

  const filteredRunning = running.filter((r) => {
    if (packageFilter !== "all" && r.package_id !== packageFilter) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return r.member_name.toLowerCase().includes(q) || r.package_name.toLowerCase().includes(q);
  });

  const packageOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of running) if (!seen.has(r.package_id)) seen.set(r.package_id, r.package_name);
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  }, [running]);

  function openMember(memberId: string) {
    navigate({ to: "/members/$memberId", params: { memberId } });
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2 gap-3">
        <h2 className="text-xl font-semibold tracking-tight">Beiträge</h2>
        <button
          onClick={() => setShowCreate(true)}
          className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
        >
          Neuer Beitrag
        </button>
      </div>
      <p className="text-sm text-slate-500 mb-6">
        Beiträge sind die Tarife deines Vereins (z.B. «Aktiv, CHF 80 pro Jahr»). Du teilst sie
        Mitgliedern zu — Vereinfach erstellt die Rechnungen.
      </p>

      {packages === null || subs === null ? (
        <p className="text-slate-400">Lädt…</p>
      ) : (
        <>
          {active.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-slate-500">
              Noch keine Beiträge angelegt.
            </div>
          ) : (
            <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
              {active.map((p) => {
                const count = runningByPackage.get(p.id) ?? 0;
                return (
                  <div key={p.id} className="flex items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{p.name}</span>
                        {p.year_start_month !== null && (
                          <span className="inline-block rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600 whitespace-nowrap">
                            Vereinsjahr ab {MONTH_NAMES[p.year_start_month - 1]}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {formatCHF(p.price_rappen)} · {monthsLabel(p.duration_months)} ·{" "}
                        {count === 0
                          ? "keine Mitglieder zugeteilt"
                          : count === 1
                            ? "1 Mitglied zugeteilt"
                            : `${count} Mitglieder zugeteilt`}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <button
                        onClick={() => setEditing(p)}
                        className="text-sm text-slate-600 hover:text-slate-900"
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => archive(p.id)}
                        className="text-sm text-slate-500 hover:text-red-600"
                      >
                        Archivieren
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {archived.length > 0 && (
            <div className="mt-4">
              <button
                onClick={() => setShowArchived((v) => !v)}
                className="text-sm text-slate-500 hover:text-slate-900"
              >
                {showArchived ? "▾" : "▸"} Archivierte Beiträge ({archived.length})
              </button>
              {showArchived && (
                <div className="mt-2 rounded-xl border border-slate-200 divide-y divide-slate-100">
                  {archived.map((p) => (
                    <div key={p.id} className="flex items-center justify-between gap-3 px-4 py-3">
                      <div className="min-w-0">
                        <div className="font-medium text-slate-500">{p.name}</div>
                        <div className="text-xs text-slate-400 mt-0.5">
                          {formatCHF(p.price_rappen)} · {monthsLabel(p.duration_months)}
                        </div>
                      </div>
                      <button
                        onClick={() => unarchive(p.id)}
                        className="text-sm text-slate-500 hover:text-slate-900 shrink-0"
                      >
                        Reaktivieren
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <section className="mt-10">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
                Laufende Beiträge
              </h3>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Mitglied suchen…"
                  className="input sm:w-56"
                />
                <select
                  value={packageFilter}
                  onChange={(e) => setPackageFilter(e.target.value)}
                  className="input sm:w-48"
                >
                  <option value="all">Alle Beiträge</option>
                  {packageOptions.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {running.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-slate-500">
                Noch keine laufenden Beiträge. Teile einem Mitglied einen Beitrag zu.
              </div>
            ) : filteredRunning.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-slate-500">
                Keine Treffer.
              </div>
            ) : (
              <>
                <div className="space-y-3 md:hidden">
                  {filteredRunning.map((r) => (
                    <div
                      key={r.id}
                      className="rounded-xl border border-slate-200 bg-white p-4 cursor-pointer active:bg-slate-50"
                      onClick={() => openMember(r.member_id)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">
                            {r.member_name}
                          </div>
                          <div className="mt-0.5 text-xs text-slate-500">{r.package_name}</div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <SubStatusChips status={r.status} autoRenew={r.auto_renew} />
                        </div>
                      </div>
                      <div className="mt-3 text-sm text-slate-700">
                        {formatPeriodRange(r.current_start, r.current_end)}
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        {coverageLabelText(r.coverage_label)}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="hidden md:block rounded-xl border border-slate-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500">
                      <tr>
                        <th className="text-left font-medium px-4 py-2.5">Mitglied</th>
                        <th className="text-left font-medium px-4 py-2.5">Beitrag</th>
                        <th className="text-left font-medium px-4 py-2.5">Zeitraum</th>
                        <th className="text-left font-medium px-4 py-2.5">Deckung</th>
                        <th className="text-left font-medium px-4 py-2.5">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRunning.map((r) => (
                        <tr
                          key={r.id}
                          className="border-t border-slate-100 cursor-pointer hover:bg-slate-50"
                          onClick={() => openMember(r.member_id)}
                        >
                          <td className="px-4 py-2.5">
                            <Link
                              to="/members/$memberId"
                              params={{ memberId: r.member_id }}
                              onClick={(e) => e.stopPropagation()}
                              className="font-medium hover:underline"
                            >
                              {r.member_name}
                            </Link>
                          </td>
                          <td className="px-4 py-2.5 text-slate-600">{r.package_name}</td>
                          <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">
                            {formatPeriodRange(r.current_start, r.current_end)}
                          </td>
                          <td className="px-4 py-2.5 text-slate-500">
                            {coverageLabelText(r.coverage_label)}
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex gap-1">
                              <SubStatusChips status={r.status} autoRenew={r.auto_renew} />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        </>
      )}

      {showCreate && (
        <PackageFormModal
          tenantId={tenantId}
          pkg={null}
          runningCount={0}
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            refresh();
          }}
        />
      )}

      {editing && (
        <PackageFormModal
          tenantId={tenantId}
          pkg={editing}
          runningCount={runningByPackage.get(editing.id) ?? 0}
          onClose={() => setEditing(null)}
          onSaved={(offer) => {
            setEditing(null);
            if (offer) setRefreshOffer(offer);
            refresh();
          }}
        />
      )}

      {refreshOffer && (
        <Modal title="Entwürfe aktualisieren?" onClose={() => setRefreshOffer(null)}>
          <p className="text-sm text-slate-600">
            {refreshOffer.ids.length === 1
              ? "1 offener Entwurf verwendet noch den alten Preis — auf den neuen aktualisieren?"
              : `${refreshOffer.ids.length} offene Entwürfe verwenden noch den alten Preis — auf den neuen aktualisieren?`}
          </p>
          <div className="mt-5 flex justify-end gap-2">
            <button
              onClick={() => setRefreshOffer(null)}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
            >
              Nicht aktualisieren
            </button>
            <button
              onClick={() => refreshDrafts(refreshOffer)}
              className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm text-white hover:bg-slate-800"
            >
              Aktualisieren
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function SubStatusChips({ status, autoRenew }: { status: string; autoRenew: number }) {
  return (
    <>
      {status === "paused" ? (
        <span className="inline-block rounded px-2 py-0.5 text-xs bg-amber-50 text-amber-700 whitespace-nowrap">
          Pausiert
        </span>
      ) : (
        <span className="inline-block rounded px-2 py-0.5 text-xs bg-emerald-50 text-emerald-700 whitespace-nowrap">
          Aktiv
        </span>
      )}
      {autoRenew === 0 && (
        <span className="inline-block rounded px-2 py-0.5 text-xs bg-slate-100 text-slate-500 whitespace-nowrap">
          läuft aus
        </span>
      )}
    </>
  );
}

function PackageFormModal({
  tenantId,
  pkg,
  runningCount,
  onClose,
  onSaved,
}: {
  tenantId: string;
  pkg: Package | null;
  runningCount: number;
  onClose: () => void;
  onSaved: (offer?: RefreshOffer) => void;
}) {
  const [name, setName] = useState(pkg?.name ?? "");
  const [price, setPrice] = useState(pkg ? (pkg.price_rappen / 100).toFixed(2) : "");
  const [duration, setDuration] = useState(pkg?.duration_months ?? 12);
  const [alignYear, setAlignYear] = useState(pkg?.year_start_month != null);
  const [month, setMonth] = useState(pkg?.year_start_month ?? 1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const durationLocked = pkg !== null && runningCount > 0;
  const durations = alignYear ? YEAR_ALIGNED_DURATIONS : DURATIONS;
  const parsedPrice = parseFloat(price.replace(",", "."));

  function toggleAlignYear(checked: boolean) {
    setAlignYear(checked);
    if (checked && !YEAR_ALIGNED_DURATIONS.includes(duration)) setDuration(12);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!Number.isFinite(parsedPrice) || parsedPrice < 0) {
      setError("Ungültiger Preis.");
      return;
    }
    const rappen = Math.round(parsedPrice * 100);
    setSubmitting(true);
    try {
      if (pkg === null) {
        await mutate<Package>(`/tenants/${tenantId}/packages`, {
          name: name.trim(),
          duration_months: duration,
          price_rappen: rappen,
          year_start_month: alignYear ? month : null,
        });
        onSaved();
      } else {
        const body: Record<string, unknown> = {
          name: name.trim(),
          price_rappen: rappen,
          year_start_month: alignYear ? month : null,
        };
        if (!durationLocked) body.duration_months = duration;
        const res = await mutate<{ package: Package; refreshable_draft_ids: string[] }>(
          `/tenants/${tenantId}/packages/${pkg.id}`,
          body,
          "PATCH",
        );
        const priceChanged = rappen !== pkg.price_rappen;
        onSaved(
          priceChanged && res.refreshable_draft_ids.length > 0
            ? { packageId: pkg.id, ids: res.refreshable_draft_ids }
            : undefined,
        );
      }
    } catch (err) {
      setError(errorMessage(err));
      setSubmitting(false);
    }
  }

  return (
    <Modal title={pkg ? "Beitrag bearbeiten" : "Neuer Beitrag"} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-4">
        <label className="block">
          <span className="text-xs font-medium text-slate-500">Name</span>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z. B. Aktiv"
            className="input mt-1"
            autoFocus
          />
        </label>

        <label className="block">
          <span className="text-xs font-medium text-slate-500">Preis (CHF)</span>
          <input
            required
            inputMode="decimal"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="80.00"
            className="input mt-1"
          />
        </label>
        {parsedPrice === 0 && (
          <p className="text-xs text-slate-500">
            CHF 0.00 = beitragsfrei (z.B. Ehrenmitglieder) — es werden keine Rechnungen erstellt.
          </p>
        )}

        <label className="block">
          <span className="text-xs font-medium text-slate-500">Laufzeit</span>
          <select
            value={duration}
            onChange={(e) => setDuration(parseInt(e.target.value, 10))}
            disabled={durationLocked}
            className="input mt-1 disabled:bg-slate-50 disabled:text-slate-400"
          >
            {durations.map((d) => (
              <option key={d} value={d}>
                {monthsLabel(d)}
              </option>
            ))}
          </select>
        </label>
        {durationLocked && (
          <p className="text-xs text-slate-500">
            Laufzeit fixiert — archivieren und neuen Beitrag anlegen.
          </p>
        )}

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={alignYear}
            onChange={(e) => toggleAlignYear(e.target.checked)}
            className="rounded border-slate-300"
          />
          An Vereinsjahr ausrichten
        </label>
        {alignYear && (
          <>
            <label className="block">
              <span className="text-xs font-medium text-slate-500">Vereinsjahr beginnt im</span>
              <select
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value, 10))}
                className="input mt-1"
              >
                {MONTH_NAMES.map((m, i) => (
                  <option key={m} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
            <p className="text-xs text-slate-500">
              Am Vereinsjahr ausgerichtete Beiträge brauchen eine Laufzeit, die ein Jahr sauber
              teilt — darum sind hier nur 1, 2, 3, 4, 6 oder 12 Monate möglich.
            </p>
          </>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {submitting ? "Speichern…" : pkg ? "Speichern" : "Beitrag anlegen"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
